// BUG-005a: the one place that decides whether a property is in the Republic of Ireland.
// Plain CommonJS so the server, the browser (CRA) and the Jest tests can all use it.
// This file exists twice, byte for byte: services/propertyCountry.js (server) and
// frontend/src/utils/propertyCountry.js (browser; CRA cannot import outside src/).
// A test fails if the two copies ever differ, so edit both together.
//
// The rule: a property is Irish when its jurisdiction is Ireland OR its currency is EUR.
// Two traps this handles:
//   * properties store jurisdiction as 'england', 'scotland', 'wales', 'northern_ireland', 'ireland',
//     but companies store 'UK' (and, in future, possibly 'Ireland'); both spellings are understood here.
//   * 'northern_ireland' is part of the UK and is NOT Irish. Only the Republic is.

const IRELAND = 'ireland';

// any spelling -> 'england' | 'scotland' | 'wales' | 'northern_ireland' | 'ireland' | 'uk' | null (unknown or empty)
function normaliseJurisdiction(raw) {
  if (raw === undefined || raw === null) return null;
  const t = String(raw).trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!t) return null;
  if (t === 'ireland' || t === 'republic_of_ireland' || t === 'ie' || t === 'roi' || t === 'eire') return IRELAND;
  if (t === 'northern_ireland' || t === 'n._ireland' || t === 'n_ireland' || t === 'ni') return 'northern_ireland';
  if (t === 'england' || t === 'scotland' || t === 'wales') return t;
  if (t === 'uk' || t === 'gb' || t === 'united_kingdom' || t === 'great_britain') return 'uk';
  return null;
}

const isEuro = (currency) => String(currency === undefined || currency === null ? '' : currency).trim().toUpperCase() === 'EUR';

// A property (or a tenant row that carries the property's jurisdiction and currency): jurisdiction ireland OR currency EUR
function isIrishProperty(p) {
  if (!p) return false;
  return normaliseJurisdiction(p.jurisdiction) === IRELAND || isEuro(p.currency);
}

// A company: the same rule. A company's 'UK' is never Irish.
function isIrishCompany(c) {
  if (!c) return false;
  return normaliseJurisdiction(c.jurisdiction) === IRELAND || isEuro(c.currency);
}

// The jurisdiction the app should use for a property: 'ireland' when it is Irish, otherwise the stored value
// ('uk' on a property is read as England), and 'england' when nothing usable is stored.
function effectiveJurisdiction(p) {
  if (isIrishProperty(p)) return IRELAND;
  const j = normaliseJurisdiction(p && p.jurisdiction);
  return !j || j === 'uk' ? 'england' : j;
}

// The default jurisdiction to offer on the Add form for a currency (the user can still change it)
function jurisdictionForCurrency(currency) {
  return isEuro(currency) ? IRELAND : 'england';
}

// What to store when a property is created: the jurisdiction the user chose when it is a known one,
// otherwise the default for the currency. Never empty.
function jurisdictionToSave(chosen, currency) {
  const j = normaliseJurisdiction(chosen);
  if (j && j !== 'uk') return j;
  return jurisdictionForCurrency(currency);
}

// The same rule for SQL. alias is the properties table alias, e.g. 'p'. Fixed text, never built from request data.
function irishPropertySql(alias) {
  if (!/^[a-z_][a-z0-9_]*$/i.test(alias)) throw new Error('bad alias');
  return `(LOWER(COALESCE(${alias}.jurisdiction, '')) = 'ireland' OR UPPER(COALESCE(${alias}.currency, '')) = 'EUR')`;
}

module.exports = {
  normaliseJurisdiction, isIrishProperty, isIrishCompany, effectiveJurisdiction,
  jurisdictionForCurrency, jurisdictionToSave, irishPropertySql,
};
